import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../Auth';
import { ObjectiveTable, TaskTable, ObjectiveCompactList, TaskCompactList } from './ObjectiveComponents';

const ChatBubble = ({ message }) => {
  const { user } = useAuth();
  const { content = '', role, agent, isError } = message;

  const parseContent = () => {
    let processedContent = content;
    const specialComponents = [];

    try {
      const blockRegex = /```(objectives-table|tasks-table|objectives-compact|tasks-compact)\n([\s\S]*?)\n```/g;
      let match;

      while ((match = blockRegex.exec(content)) !== null) {
        const placeholder = `[SPECIAL_BLOCK_${specialComponents.length}]`;
        try {
          const tableData = JSON.parse(match[2]);
          specialComponents.push({
            placeholder,
            type: match[1],
            data: tableData,
            original: match[0],
          });
        } catch (error) {
          console.error("Erreur lors de l'analyse du bloc spécial:", error);
        }
        processedContent = processedContent.replace(match[0], placeholder);
      }
    } catch (error) {
      console.error('Erreur lors du parsing du contenu:', error);
      return { processedContent: content, specialComponents: [] };
    }

    return { processedContent, specialComponents };
  };

  const getBubbleClass = () => {
    if (role === 'user') {
      return 'ml-auto bg-emerald-100/90 text-emerald-950 border border-emerald-200';
    }
    if (role === 'system') {
      return 'mx-auto bg-slate-200/90 text-slate-700 border border-slate-300';
    }
    if (isError) {
      return 'bg-rose-50 text-rose-700 border border-rose-200';
    }
    return 'bg-white text-slate-900 border border-indigo-100/70';
  };

  const getAgentIcon = () => {
    if (role === 'user') {
      if (user?.picture) {
        return (
          <img
            src={user.picture}
            alt={user.name || 'Utilisateur'}
            className="h-8 w-8 rounded-full border-2 border-emerald-300 object-cover"
          />
        );
      }
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-sm font-semibold text-white">
          {user?.name?.charAt(0) || 'U'}
        </div>
      );
    }

    if (role === 'system') {
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-500 text-white">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    }

    if (agent === 'objective') {
      return (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
      );
    }

    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
        <img src="/Nexus_icon.png" alt="Nexus" className="h-7 w-7" />
      </div>
    );
  };

  const renderContent = () => {
    const { processedContent, specialComponents } = parseContent();
    if (!specialComponents.length) {
      return (
        <ReactMarkdown className="prose prose-sm prose-slate max-w-none break-words">
          {content}
        </ReactMarkdown>
      );
    }

    const parts = processedContent.split(/\[SPECIAL_BLOCK_\d+\]/);
    const placeholders = processedContent.match(/\[SPECIAL_BLOCK_\d+\]/g) || [];

    const fragments = [];
    parts.forEach((part, index) => {
      if (part) {
        fragments.push(
          <ReactMarkdown key={`text-${index}`} className="prose prose-sm prose-slate max-w-none break-words">
            {part}
          </ReactMarkdown>
        );
      }

      if (index < placeholders.length) {
        const componentIndex = parseInt(placeholders[index].match(/\d+/)[0], 10);
        const component = specialComponents[componentIndex];
        if (component?.type === 'objectives-table') {
          fragments.push(
            <ObjectiveTable key={`objectives-${componentIndex}`} objectives={component.data?.objectives || []} />
          );
        } else if (component?.type === 'tasks-table') {
          fragments.push(
            <TaskTable key={`tasks-${componentIndex}`} tasks={component.data?.tasks || []} />
          );
        } else if (component?.type === 'objectives-compact') {
          fragments.push(
            <ObjectiveCompactList key={`objectives-compact-${componentIndex}`} objectives={component.data?.objectives || []} />
          );
        } else if (component?.type === 'tasks-compact') {
          fragments.push(
            <TaskCompactList key={`tasks-compact-${componentIndex}`} tasks={component.data?.tasks || []} />
          );
        }
      }
    });

    return fragments;
  };

  return (
    <div className={`flex items-start gap-3 ${role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
      {getAgentIcon()}
      <div className={`max-w-[90%] sm:max-w-[80%] lg:max-w-[72%] rounded-2xl border px-4 py-3 shadow-sm ${getBubbleClass()}`}>
        {renderContent()}
      </div>
    </div>
  );
};

ChatBubble.propTypes = {
  message: PropTypes.shape({
    content: PropTypes.string,
    role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    agent: PropTypes.string,
    isError: PropTypes.bool,
  }).isRequired,
};

export default ChatBubble;
